<script setup>
import { computed, onMounted, ref } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import PainelAPI from 'dashboard/api/painel';
import StatusChip from './StatusChip.vue';

const emit = defineEmits(['open-appointment']);

const store = useStore();
const { t, locale } = useI18n();

const appointments = ref([]);
const loadedUntil = ref(null);
const isFetching = ref(false);
const hasMore = ref(true);

const statusFilter = computed(
  () => store.getters['painelView/getStatusFilter']
);

const today = () => new Date().toLocaleDateString('en-CA');

const addDays = (dateStr, n) => {
  const d = new Date(`${dateStr}T00:00:00`);
  d.setDate(d.getDate() + n);
  return d.toLocaleDateString('en-CA');
};

const loadMore = async () => {
  if (isFetching.value) return;
  isFetching.value = true;
  try {
    const from = loadedUntil.value ? addDays(loadedUntil.value, 1) : today();
    const to = addDays(from, 6);
    const { data } = await PainelAPI.fetchAppointments({
      from,
      to,
      include_cancelled: true,
    });
    const fetched = data.appointments || [];
    appointments.value.push(...fetched);
    loadedUntil.value = to;
    hasMore.value = fetched.length > 0;
  } finally {
    isFetching.value = false;
  }
};

const groupedByDate = computed(() => {
  const filtered = appointments.value.filter(a =>
    statusFilter.value.has(a.status)
  );
  const groups = {};
  filtered.forEach(appt => {
    const day = appt.starts_at.slice(0, 10);
    if (!groups[day]) groups[day] = [];
    groups[day].push(appt);
  });
  return groups;
});

const sortedDates = computed(() => Object.keys(groupedByDate.value).sort());

const formatDate = dateStr =>
  new Date(`${dateStr}T00:00:00`).toLocaleDateString(
    locale.value.replace('_', '-'),
    { weekday: 'long', day: 'numeric', month: 'long' }
  );

const formatTime = iso =>
  new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

const confirmingIds = ref(new Set());

const patchItem = updatedAppt => {
  const idx = appointments.value.findIndex(a => a.id === updatedAppt.id);
  if (idx >= 0) {
    appointments.value.splice(idx, 1, updatedAppt);
  }
};

const confirmInline = async appt => {
  if (confirmingIds.value.has(appt.id)) return;
  confirmingIds.value = new Set([...confirmingIds.value, appt.id]);
  try {
    const { data } = await PainelAPI.confirmAppointment(appt.id);
    patchItem(data.payload);
  } finally {
    confirmingIds.value = new Set(
      [...confirmingIds.value].filter(id => id !== appt.id)
    );
  }
};

defineExpose({ patchItem });

onMounted(loadMore);
</script>

<template>
  <div class="flex flex-1 flex-col overflow-y-auto">
    <div class="flex flex-col gap-6 p-6">
      <div
        v-if="sortedDates.length === 0 && !isFetching"
        class="py-12 text-center text-n-slate-10"
      >
        {{ t('PAINEL.APPOINTMENTS.HEADER.EMPTY_AGENDA') }}
      </div>

      <div v-for="dateKey in sortedDates" :key="dateKey">
        <div
          class="mb-2 text-xs font-semibold uppercase tracking-wide text-n-slate-10 capitalize"
        >
          {{ formatDate(dateKey) }}
        </div>
        <div class="flex flex-col gap-2">
          <div
            v-for="appt in groupedByDate[dateKey]"
            :key="appt.id"
            class="flex cursor-pointer items-center gap-3 rounded-lg border border-n-strong bg-n-surface-2 p-3 hover:bg-n-slate-3"
            @click="emit('open-appointment', appt)"
          >
            <div class="min-w-0 flex-1">
              <div class="truncate text-sm font-medium text-n-slate-12">
                {{
                  appt.contact?.name ||
                  appt.title ||
                  t('PAINEL.APPOINTMENTS.NO_TITLE')
                }}
              </div>
              <div class="text-xs text-n-slate-11">
                {{ formatTime(appt.starts_at) }}
                {{ t('PAINEL.APPOINTMENTS.TIME_SEPARATOR') }}
                {{ formatTime(appt.ends_at) }}
              </div>
            </div>
            <StatusChip :status="appt.status" />
            <button
              v-if="appt.status === 'pending'"
              type="button"
              class="shrink-0 rounded bg-n-brand px-3 py-1.5 text-xs font-semibold text-white hover:opacity-90 disabled:opacity-50"
              :disabled="confirmingIds.has(appt.id)"
              @click.stop="confirmInline(appt)"
            >
              {{ t('PAINEL.APPOINTMENTS.CONFIRM') }}
            </button>
          </div>
        </div>
      </div>

      <div v-if="isFetching" class="py-4 text-center text-sm text-n-slate-10">
        {{ t('PAINEL.APPOINTMENTS.HEADER.LOADING') }}
      </div>

      <button
        v-if="hasMore && !isFetching"
        type="button"
        class="mx-auto rounded border border-n-strong px-4 py-2 text-sm text-n-slate-11 hover:bg-n-slate-3"
        @click="loadMore"
      >
        {{ t('PAINEL.APPOINTMENTS.HEADER.LOAD_MORE') }}
      </button>
    </div>
  </div>
</template>
